import { z } from 'zod';

import {
    WorkItemStatusSchema,
    WorkItemType,
    WorkItemTypeSchema,
} from '@/api/common/common.models';
import { isoDateString } from '@/lib/zod';

export const PreliminaryFormModelSchema = z
    .object({
        name: z.string().min(1, { message: 'Name is required' }).max(100, {
            message: 'Name cannot be longer than 100 characters',
        }),
        type: WorkItemTypeSchema,
        status: WorkItemStatusSchema,
        dateStarted: isoDateString.nullable().optional(),
        anticipatedCompletionDate: isoDateString.nullable().optional(),
        value: z
            .number({
                error: 'Item value must be a number',
            })
            .nonnegative({
                error: 'Item value cannot be negative',
            })
            .nullable()
            .optional(),
        managerName: z
            .string()
            .max(100, {
                error: 'Manager name cannot be longer than 100 characters',
            })
            .nullable()
            .optional(),
        estimatorName: z
            .string()
            .max(100, {
                error: 'Estimator name cannot be longer than 100 characters',
            })
            .nullable()
            .optional(),
        surveyorName: z
            .string()
            .max(100, {
                error: 'Surveyor name cannot be longer than 100 characters',
            })
            .nullable()
            .optional(),
    })
    .refine(
        (data) => {
            if (
                data.type === WorkItemType.Contracted ||
                data.type === WorkItemType.Variation
            ) {
                return typeof data.value === 'number';
            }
            return true;
        },
        {
            message:
                'Item value is required for Contracted and Variation items',
            path: ['value'],
        },
    )
    .refine(
        (data) => {
            if (data.dateStarted && data.anticipatedCompletionDate) {
                const startDate = new Date(data.dateStarted);
                const endDate = new Date(data.anticipatedCompletionDate);
                return endDate >= startDate;
            }
            return true;
        },
        {
            message: 'Anticipated end date cannot be before start date',
            path: ['anticipatedCompletionDate'],
        },
    );

export const PreliminarySchema = PreliminaryFormModelSchema.safeExtend({
    id: z.string(),
    dateModified: isoDateString,
    profitabilityPercentage: z.number().int().nullable().optional(),
    totalPreliminaryCost: z.number().nonnegative().nullable().optional(),
});

export const PreliminaryDetailSchema = PreliminarySchema.safeExtend({
    profitabilityPercentage: z.number().int().nullable().optional(),
    totalLabourCost: z.number().nonnegative().nullable().optional(),
    totalMaterialsCost: z.number().nonnegative().nullable().optional(),
    totalMiscellaneousCost: z.number().nonnegative().nullable().optional(),
    totalPreliminaryCost: z.number().nonnegative().nullable().optional(),
    costEntriesCount: z.number().int().nonnegative().nullable().optional(),
    snaggingEntriesCount: z.number().int().nonnegative().nullable().optional(),
});

export type Preliminary = z.infer<typeof PreliminarySchema>;
export type PreliminaryDetail = z.infer<typeof PreliminaryDetailSchema>;
export type PreliminaryFormModel = z.infer<typeof PreliminaryFormModelSchema>;
